import { ICriteria, INonProductionDay } from "us.collection.admin/interfaces";
import moment from "moment";
import {
  NonProductionDayTypeInterface,
  criteria,
  NonProductionDataType,
  dateFormat,
  RedDay,
  RedDayResponse,
} from "us.collection.admin/constants";
import { Guid } from "guid-typescript";

/**
 * @function
 * @description add or update red days to current redDays state
 * @param {Array<INonProductionDay>} redDays current redDays
 * @param {INonProductionDay} day new or existing day
 * @returns  {Array<INonProductionDay>}
 */
export const addOrUpdateRedDays = (
  redDays: Array<INonProductionDay>,
  day: INonProductionDay,
  uniqueKey: Guid
): Array<INonProductionDay> => {
  try {
    const { key } = day;
    if (!key.isEmpty()) {
      let isMatched = false;
      redDays = redDays.map((redDay: INonProductionDay) => {
        if (key.equals(redDay.key)) {
          isMatched = true;
          return day;
        } else {
          return redDay;
        }
      });
      if (!isMatched) {
        redDays.push(day);
      }
    } else {
      redDays.push({ ...day, key: uniqueKey });
    }
    return [...redDays];
  } catch (error) {
    return [...redDays];
  }
};

/**
 * @function
 * @description get Criteria array object
 * @param {NonProductionDayTypeInterface} selectedCriteria selected criteria
 * @returns {Array<ICriteria>}
 */
export const getCriteria = (
  selectedCriteria: NonProductionDayTypeInterface
): Array<ICriteria> => {
  try {
    return criteria.map(({ type, value }: ICriteria) => ({
      type,
      value: type == selectedCriteria ? true : false,
    }));
  } catch (error) {
    return criteria;
  }
};

/**
 * @function
 * @description get formatted red days
 * @param {RedDayResponse} nonProductionDays response params
 * @returns {Array<ICriteria>}
 */
export const getFormattedRedDays = (
  nonProductionDays: Array<RedDayResponse>
): Array<RedDay> => {
  try {
    if(Array.isArray(nonProductionDays)){
      return nonProductionDays.map((day) => ({
      id: day.id,
      startDate: day.startDate, 
      endDate: day.endDate, 
      reason: day.reason,
      nonEhf: day.isNonEhfDay,
      nonSms: day.isNonSMSDay,
      nonPrinting: day.isNonPrintDay,
      nonEmail: day.isNonEmailDay,
    }));
    }else{
      return []
    }
  } catch (error) {
    return [];
  }
};

/**
 * @function
 * @description get Red Day InitialValues
 * @param {NonProductionDayTypeInterface} selectedCriteria selected criteria
 * @returns
 */
export const getRedDayInitialValues = (
  selectedCriteria: NonProductionDayTypeInterface,
  selectedYear: number
): INonProductionDay => {
  try {
    const currentYear = moment().year();
    const tomorrow = moment().add(1, "day");
    const startOfYear = moment(`${selectedYear}-01-01`);
    return {
      id: -1,
      key: Guid.createEmpty(),
      dateRange:
        currentYear == selectedYear
          ? [tomorrow, tomorrow]
          : [startOfYear, startOfYear],
      reason: "",
      criteria: getCriteria(selectedCriteria),
    };
  } catch (error) {
    return {
      id: -1,
      key: Guid.createEmpty(),
      dateRange: [],
      reason: "",
      criteria: [],
    };
  }
};

/**
 * @function
 * @description remove red day
 * @param {Array<INonProductionDay>} redDays all redDays
 * @param {number} removeDayId day Id
 * @returns {Array<INonProductionDay>}
 */
export const removeRedDays = (
  redDays: Array<INonProductionDay>,
  removeDayId: Guid
): Array<INonProductionDay> => {
  try {
    return redDays.filter(
      ({ key }: INonProductionDay) => !key.equals(removeDayId)
    );
  } catch (error) {
    return redDays;
  }
};

/**
 * @function
 * @description add or update red days to current redDays state
 * @param {Array<RedDay>} redDays all redDays
 * @param {RedDay} day new or existing day
 * @returns {NonProductionDataType}
 */
export const addOrUpdateRedDayRelevantCriteria = (
  redDays: Array<RedDay>,
  day: INonProductionDay
): Array<RedDay> => {
  try {
    const criteriaMap = day.criteria.reduce((acc, curr) => {
      acc[curr.type] = curr.value;
      return acc;
    }, {} as Record<string, boolean>);

    const redDay: RedDay = {
      id: day.id,
      startDate: day.dateRange[0]?.toString() || "",
      endDate: day.dateRange[1]?.toString() || "",
      reason: day.reason,
      nonEhf: criteriaMap["nonEhf"] || false,
      nonSms: criteriaMap["nonSms"] || false,
      nonPrinting: criteriaMap["nonPrinting"] || false,
      nonEmail: criteriaMap["nonEmail"] || false,
    };
    redDays.push(redDay);
    return redDays;
  } catch (error) {
    return redDays;
  }
};

/**
 * @function
 * @description remove red day other criteria
 * @param {Array<INonProductionDay>} redDays all redDays
 * @param {INonProductionDay} day new or existing day
 * @returns {Array<INonProductionDay>}
 */
const removeRedDayFromOtherCriteria = (
  redDays: Array<INonProductionDay>,
  day: INonProductionDay
): Array<INonProductionDay> => {
  try {
    const { reason } = day;
    const key: undefined | Guid = redDays.find(
      (redDay: INonProductionDay) => redDay.reason == reason
    )?.key;
    return key ? removeRedDays(redDays, key) : redDays;
  } catch (error) {
    return redDays;
  }
};
